/**
 * Config for the customer-auth / customer-profile / orders / print-orders APIs the account
 * dashboard needs (login, orders, quotes, POD) — distinct from services/api.js's public,
 * no-login quote-request helpers, which stay as they are.
 */
import { getApiBaseUrl } from '../services/api';

export const API_BASE_URL = getApiBaseUrl();

const PORTAL_SLUG = 'noosa-t-shirts';

let _businessIdResolved;
let _businessIdInflight;

/**
 * Single-tenant equivalent of the multi-tenant resolveShopBusinessId() pattern used elsewhere in
 * the monorepo — resolves Noosa's real business UUID via the same portal-slug lookup
 * PortalContext.js already uses, so customer-auth signup/login always has a real businessId
 * without needing a REACT_APP_BUSINESS_ID env var to be kept in sync.
 */
export async function resolveShopBusinessId() {
  if (_businessIdResolved !== undefined) return _businessIdResolved;
  if (_businessIdInflight) return _businessIdInflight;
  _businessIdInflight = (async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/portals/slug/${PORTAL_SLUG}`);
      if (!res.ok) throw new Error('Portal not found');
      const data = await res.json();
      _businessIdResolved = data?.portal?.business?.id || null;
    } catch {
      _businessIdResolved = null;
    } finally {
      _businessIdInflight = null;
    }
    return _businessIdResolved;
  })();
  return _businessIdInflight;
}

export const API_ENDPOINTS = {
  customerAuth: {
    signup: `${API_BASE_URL}/customer-auth/signup`,
    login: `${API_BASE_URL}/customer-auth/login`,
  },
  customerProfile: {
    me: `${API_BASE_URL}/customer-profile/me`,
    orders: `${API_BASE_URL}/customer-profile/orders`,
    orderTimeline: (orderId) => `${API_BASE_URL}/customer-profile/orders/${orderId}/timeline`,
    orderReceived: (orderId) => `${API_BASE_URL}/customer-profile/orders/${orderId}/received`,
    salesQuotes: `${API_BASE_URL}/customer-profile/sales-quotes`,
    salesQuoteApprove: (id) => `${API_BASE_URL}/customer-profile/sales-quotes/${id}/approve`,
    salesQuoteDecline: (id) => `${API_BASE_URL}/customer-profile/sales-quotes/${id}/decline`,
  },
  printOrders: {
    pricing: (businessId) => `${API_BASE_URL}/print-orders/pricing/${businessId}`,
    intake: `${API_BASE_URL}/print-orders/intake`,
    checkout: (orderId) => `${API_BASE_URL}/print-orders/${orderId}/checkout`,
  },
};
